import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DS2 } from '../../../theme/designTokens';
import { useFriends } from '../../../context/FriendContext';
import { useChatDock } from '../../../context/ChatDockContext';
import { PlayerAvatar } from '../../ui/PlayerAvatar';
import { UnreadBadge } from '../../ui/UnreadBadge';
import { PresenceDot } from '../../social/PresenceDot';
import { ImageLightbox } from '../../ui/ImageLightbox';
import { DirectMessageBubbles } from './DirectMessageBubbles';
import { DirectMessageComposer } from './DirectMessageComposer';
import { useTranslation } from '../../../i18n/react';
import type { FriendListItem, SocialUser } from '../../../types/social';

const WINDOW_WIDTH = 320;
/** Pixel slack from the list bottom within which auto-scroll-to-latest is
 *  allowed. Further up = the user is reading history; do not fight them. */
const NEAR_BOTTOM_PX = 80;

function MinimizeIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 14h14" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 5l14 14M19 5L5 19" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function HeaderButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 26, borderRadius: 6, border: 'none',
        background: 'transparent', cursor: 'pointer', flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

export function presenceLabelKey(status: FriendListItem['status'] | undefined): 'chat.online' | 'chat.inGame' | 'chat.offline' {
  if (status === 'online')  return 'chat.online';
  if (status === 'in_game') return 'chat.inGame';
  return 'chat.offline';
}

interface Props {
  friendId: number;
  /** Minimized windows STAY MOUNTED and are hidden — the composer draft DOM,
   *  scroll position and loaded history all survive minimize/restore. */
  minimized: boolean;
  /** Layer-owned ref so focus can be moved onto the window on open/restore. */
  rootRef?: (el: HTMLDivElement | null) => void;
}

/**
 * One docked desktop conversation window. Data flows exclusively through
 * FriendContext (loadThread / loadOlderMessages / markConversationRead /
 * sendMessage) — hydration never marks read; the explicit Foundation-Slice
 * read endpoint is called only while this window is expanded, focused,
 * visible, the document is visible and the route is not suppressed.
 */
export function ChatDockWindow({ friendId, minimized, rootRef }: Props) {
  const { t } = useTranslation('messages');
  const {
    threads, threadMeta, conversations, friends,
    loadThread, loadOlderMessages, markConversationRead, sendMessage, loadConversations,
  } = useFriends();
  const {
    focusedId, suppressed, documentVisible,
    focusConversation, minimizeConversation, closeConversation,
    getDraft, setDraft,
  } = useChatDock();

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const pendingPrependRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const lastMessageIdRef = useRef<number | null>(null);
  const readInFlightRef = useRef(false);

  const messages = threads[friendId] ?? [];
  const meta = threadMeta[friendId];
  const conversation = conversations.find(c => c.friend.id === friendId);
  const friendEntry = friends.find(f => f.friend.id === friendId);
  const friendInfo: SocialUser | null = conversation?.friend ?? friendEntry?.friend ?? null;
  const status = friendEntry?.status;
  const name = friendInfo?.display_name ?? '';
  const unread = conversation?.unread_count ?? 0;
  const focused = focusedId === friendId;

  // ── Hydration: newest page WITHOUT marking read (dock-safe seam). ─────────
  useEffect(() => {
    if (!meta?.loaded && !meta?.loading) void loadThread(friendId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendId]);

  // ── Read semantics: mark read ONLY while expanded + focused + visible +
  //    document visible + not route-suppressed, and only when there is
  //    something unread and no acknowledgement already in flight. ────────────
  const readEligible = !minimized && focused && documentVisible && !suppressed;
  useEffect(() => {
    if (!readEligible || unread === 0 || readInFlightRef.current) return;
    readInFlightRef.current = true;
    void markConversationRead(friendId)
      .catch(() => {})  // unread stays; a later eligibility change retries
      .finally(() => { readInFlightRef.current = false; });
  }, [readEligible, unread, friendId, markConversationRead]);

  // ── Scroll: stick to latest only when the user is already near the bottom
  //    (or the newest message is their own); preserve position on prepends. ──
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || messages.length === 0) return;
    const lastId = messages[messages.length - 1].id;
    if (pendingPrependRef.current) {
      const { scrollHeight, scrollTop } = pendingPrependRef.current;
      pendingPrependRef.current = null;
      el.scrollTop = el.scrollHeight - scrollHeight + scrollTop;
    } else if (lastId !== lastMessageIdRef.current && nearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    lastMessageIdRef.current = lastId;
  }, [messages]);

  function handleScroll() {
    const el = listRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  }

  function handleLoadOlder() {
    const el = listRef.current;
    if (el) pendingPrependRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop };
    void loadOlderMessages(friendId);
  }

  async function handleSend(body: string, image: File | undefined) {
    await sendMessage(friendId, body, image);
    nearBottomRef.current = true;  // own sends always reveal the latest message
    if (!conversations.some(c => c.friend.id === friendId)) void loadConversations();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Escape minimizes ONLY this window. The shared image viewer owns Escape
    // while it is open, and DS2Modal intercepts Escape at window capture (and
    // makes the background inert) — so neither can be interfered with here.
    if (e.key !== 'Escape' || lightboxUrl) return;
    e.stopPropagation();
    minimizeConversation(friendId);
  }

  const statusKey = presenceLabelKey(status);

  return (
    <div
      ref={rootRef}
      role="complementary"
      aria-label={t('chat.windowAria', { name })}
      tabIndex={-1}
      hidden={minimized}
      onKeyDown={handleKeyDown}
      onFocusCapture={() => { if (!focused) focusConversation(friendId); }}
      onMouseDownCapture={() => { if (!focused) focusConversation(friendId); }}
      style={{
        width:         WINDOW_WIDTH,
        height:        'min(440px, calc(100dvh - 96px))',
        display:       minimized ? 'none' : 'flex',
        flexDirection: 'column',
        background:    '#101010',
        border:        `1px solid ${focused ? 'rgba(201,168,76,0.85)' : 'rgba(201,168,76,0.4)'}`,
        borderBottom:  'none',
        borderRadius:  '12px 12px 0 0',
        boxShadow:     focused ? '0 -4px 28px rgba(201,168,76,0.12)' : '0 -4px 20px rgba(0,0,0,0.5)',
        overflow:      'hidden',
        outline:       'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px 8px 10px',
          borderBottom: '1px solid rgba(201,168,76,0.25)',
          background: 'rgba(201,168,76,0.07)', flexShrink: 0,
        }}
      >
        <span style={{ position: 'relative', flexShrink: 0 }}>
          <PlayerAvatar avatarKey={friendInfo?.avatar_key} name={name} size={26} />
          {status && (
            <PresenceDot
              status={status}
              size={9}
              border="2px solid #101010"
              style={{ position: 'absolute', right: -1, bottom: -1 }}
            />
          )}
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span
            title={name}
            style={{
              fontFamily: DS2.font.heading, fontSize: 13, fontWeight: 700, color: DS2.color.gold,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {name}
          </span>
          <span style={{ fontFamily: DS2.font.body, fontSize: 10, color: '#8A8A8A' }}>
            {t(statusKey)}
          </span>
        </span>
        {unread > 0 && (
          <span aria-label={t('chat.unreadAria', { count: unread })}>
            <UnreadBadge count={unread} />
          </span>
        )}
        <HeaderButton label={t('chat.minimize')} onClick={() => minimizeConversation(friendId)}>
          <MinimizeIcon />
        </HeaderButton>
        <HeaderButton label={t('chat.close')} onClick={() => closeConversation(friendId)}>
          <CloseIcon />
        </HeaderButton>
      </div>

      {/* History */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {meta?.hasMore && (
          <button
            type="button"
            onClick={handleLoadOlder}
            disabled={meta.loading}
            style={{
              alignSelf: 'center', flexShrink: 0, border: '1px solid rgba(201,168,76,0.35)',
              background: 'transparent', color: DS2.color.gold, borderRadius: 999,
              fontFamily: DS2.font.body, fontSize: 11, padding: '4px 12px',
              cursor: meta.loading ? 'default' : 'pointer', opacity: meta.loading ? 0.6 : 1,
            }}
          >
            {meta.loading ? t('chat.loadingMessages') : t('chat.loadOlder')}
          </button>
        )}
        {!meta?.loaded && meta?.loading ? (
          <div style={{ margin: 'auto', color: '#8A8A8A', fontSize: 12, fontFamily: DS2.font.body }}>{t('chat.loadingMessages')}</div>
        ) : !meta?.loaded && meta?.error ? (
          <div style={{ margin: 'auto', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 12, fontFamily: DS2.font.body, color: '#CCCCCC' }}>{t('chat.couldNotLoad')}</p>
            <button
              type="button"
              onClick={() => void loadThread(friendId)}
              style={{
                marginTop: 8, border: '1px solid rgba(201,168,76,0.35)', background: 'transparent',
                color: DS2.color.gold, borderRadius: 999, fontFamily: DS2.font.body, fontSize: 11,
                padding: '4px 12px', cursor: 'pointer',
              }}
            >
              {t('chat.retry')}
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div style={{ margin: 'auto', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 12.5, fontFamily: DS2.font.body, color: '#CCCCCC' }}>{t('noMessagesYet')}</p>
            <p style={{ margin: '5px 0 0', fontSize: 11.5, fontFamily: DS2.font.body, color: '#8A8A8A' }}>{t('sayHello', { name })}</p>
          </div>
        ) : (
          <DirectMessageBubbles messages={messages} friendId={friendId} onImageClick={setLightboxUrl} compact />
        )}
      </div>

      {/* Composer — the shared implementation (send-once, validation, errors) */}
      <div style={{ padding: '8px 10px 10px', borderTop: '1px solid rgba(201,168,76,0.2)', flexShrink: 0 }}>
        <DirectMessageComposer
          onSend={handleSend}
          compact
          initialHtml={getDraft(friendId)}
          onDraftChange={html => setDraft(friendId, html)}
        />
      </div>

      {lightboxUrl && <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}
